import React, { useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Tooltip, Typography,
} from '@mui/material';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import { useSnackbar } from 'notistack';
import { api } from '../services/api';
import { auth } from '../services/auth';
import {
  FEFE_SEGMENT_ID,
  LEGACY_WILDCARD_SEGMENT_ID,
  deviceSupportsApFailsafe,
  deviceHasApFailsafe,
  deviceSegmentApIds,
  apSegmentIdsForTree,
  segmentIdLabel,
} from '../utils/deviceLampActions';

/** Confirms turning accept-all on or off before the segment change is sent. */
export function AcceptAllClientsConfirmDialog({
  open,
  enabling,
  targetLabel,
  onCancel,
  onConfirm,
}) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        {enabling ? 'Turn on accept all clients' : 'Turn off accept all clients'}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {`This is a segment operation${targetLabel ? ` on ${targetLabel}` : ''}.`}
        </DialogContentText>
        <DialogContentText sx={{ mt: 1.5 }}>
          {enabling
            ? 'Accept all bypasses the segments defined in the system and may result in connectivity degradation.'
            : 'Turning this off applies strict segment operation. Devices might be left with no way to connect to the tree.'}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="inherit">Cancel</Button>
        <Button onClick={onConfirm} color="warning" variant="contained">
          {enabling ? 'Turn on' : 'Turn off'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Admin-only toggle for 0xFEFE on segment_id_ap ("accept all clients").
 * Renders nothing for non-admins or devices that do not report a segment.
 * onChanged receives the updated segment from the API.
 */
const ApFailsafeButton = ({ device, treeId, onChanged, stopPropagation = false, fullWidth = false, sx }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!auth.isAdmin() || !deviceSupportsApFailsafe(device)) return null;

  const enabled = treeId
    ? apSegmentIdsForTree(device, treeId).includes(FEFE_SEGMENT_ID)
    : deviceHasApFailsafe(device);

  const applyToggle = async () => {
    if (saving) return;
    const enable = !enabled;
    setSaving(true);
    try {
      const res = await api.setDeviceApFailsafe(device.serial_number, enable, treeId);
      if (onChanged) await onChanged(res || null);
      let msg = enable ? 'Accept all clients enabled (FEFE added)' : 'Accept all clients disabled (FEFE removed)';
      if (!res?.config_changed) msg += ' — configuration already matched';
      else if (!res?.pushed && res?.connected) msg += ' — saved; the device did not accept the push';
      else if (!res?.pushed) msg += ' — saved; device offline, applies on reconnect';
      enqueueSnackbar(msg, { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(err?.message || 'Failed to update FEFE', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Tooltip
      title={treeId
        ? (enabled
          ? 'This radio accepts children from any segment. Click to remove 0xFEFE from this radio only.'
          : 'Add 0xFEFE on this radio so it accepts children from any segment.')
        : (enabled
          ? 'Every AP radio accepts children from any segment. Click to remove 0xFEFE from all of them.'
          : 'Add 0xFEFE on every AP radio so they accept children from any segment.')}
    >
      <span style={fullWidth ? { flex: 1, display: 'flex' } : { display: 'inline-flex' }}>
        <Button
          variant={enabled ? 'contained' : 'outlined'}
          color="warning"
          size="small"
          fullWidth={fullWidth}
          disabled={saving}
          startIcon={saving
            ? <CircularProgress size={12} color="inherit" />
            : <AllInclusiveIcon sx={{ fontSize: '0.9rem' }} />}
          onClick={(e) => {
            if (stopPropagation) e.stopPropagation();
            if (saving) return;
            setConfirmOpen(true);
          }}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.75rem',
            py: 0.5,
            px: 1.5,
            borderWidth: 1.5,
            '&:hover': { borderWidth: 1.5 },
            ...sx,
          }}
        >
          {enabled ? 'Accept all clients: on' : 'Accept all clients: off'}
        </Button>
      </span>
    </Tooltip>
    <AcceptAllClientsConfirmDialog
      open={confirmOpen}
      enabling={!enabled}
      targetLabel={treeId ? `${treeId} radio` : 'every AP radio'}
      onCancel={() => setConfirmOpen(false)}
      onConfirm={() => {
        setConfirmOpen(false);
        applyToggle();
      }}
    />
    </>
  );
};

/** AP segment chips plus the admin accept-all toggle, for one radio card. */
export function RadioApSegments({ device, treeId, onChanged }) {
  if (!deviceSupportsApFailsafe(device)) return null;
  const ids = treeId ? apSegmentIdsForTree(device, treeId) : deviceSegmentApIds(device);
  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'background.default',
      p: 1,
      borderRadius: 1,
      gap: 0.5,
    }}>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        AP segments
      </Typography>
      <Box sx={{
        bgcolor: 'background.paper',
        p: 0.75,
        borderRadius: 0.5,
        border: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.75,
        alignItems: 'center',
      }}>
        {ids.length === 0 && (
          <Typography variant="body2" color="text.secondary">None</Typography>
        )}
        {ids.map((id) => (
          <Tooltip
            key={id}
            title={
              id === FEFE_SEGMENT_ID
                ? '0xFEFE — this AP accepts children from any segment'
                : id === LEGACY_WILDCARD_SEGMENT_ID
                  ? '0xFFFF — all segments'
                  : `Segment ${id}`
            }
          >
            <Chip
              size="small"
              label={id === LEGACY_WILDCARD_SEGMENT_ID ? 'All' : segmentIdLabel(id)}
              color={id === FEFE_SEGMENT_ID ? 'warning' : 'default'}
              variant={id === FEFE_SEGMENT_ID ? 'filled' : 'outlined'}
              sx={{ height: 22, fontFamily: 'monospace', fontSize: '0.7rem' }}
            />
          </Tooltip>
        ))}
        {treeId && <ApFailsafeButton device={device} treeId={treeId} onChanged={onChanged} />}
      </Box>
    </Box>
  );
}

export default ApFailsafeButton;
